/**
 * Bulk Brief Service
 *
 * Handles AI-powered bulk brief generation and A/B test suggestions
 * for the Bulk Content Planner feature.
 */

import { buildPromotionContext } from './promotionService';
import { buildProductContext } from './productService';
import { COPY_STYLE_GUIDE } from './copyStyleGuide';

const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY;
const GEMINI_MODEL = import.meta.env.VITE_GEMINI_MODEL || 'gemini-2.5-flash';
const GEMINI_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent`;

// ============================================================================
// RATE LIMITING UTILITIES
// ============================================================================

const RATE_LIMIT_CONFIG = {
    delayBetweenRequests: 4000,  // 4 seconds between requests
    maxRetries: 8,
    initialRetryDelay: 10000,   // 10 seconds initial retry delay
    maxRetryDelay: 120000       // 2 minutes max retry delay
};

// ============================================================================
// REQUEST USAGE TRACKER
// ============================================================================

const usageTracker = {
    requests: [],  // Array of { timestamp, inputTokens }

    /**
     * Estimate input token count from a request body.
     * Uses ~4 chars per token as a rough approximation.
     */
    estimateInputTokens(options) {
        try {
            const body = JSON.parse(options?.body || '{}');
            const text = body?.contents?.[0]?.parts?.[0]?.text || '';
            return Math.ceil(text.length / 4);
        } catch {
            return 0;
        }
    },

    /**
     * Record a request.
     */
    record(inputTokens) {
        this.requests.push({ timestamp: Date.now(), inputTokens });
    },

    /**
     * Get stats for the last 60 seconds.
     */
    getMinuteStats() {
        const oneMinuteAgo = Date.now() - 60000;
        const recentRequests = this.requests.filter(r => r.timestamp >= oneMinuteAgo);
        return {
            requestsInLastMinute: recentRequests.length,
            inputTokensInLastMinute: recentRequests.reduce((sum, r) => sum + r.inputTokens, 0),
            totalRequestsThisSession: this.requests.length,
            totalInputTokensThisSession: this.requests.reduce((sum, r) => sum + r.inputTokens, 0)
        };
    }
};

/**
 * Delay execution for specified milliseconds
 */
function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Make an API request with retry logic for rate limiting
 */
async function fetchWithRetry(url, options, retryCount = 0) {
    try {
        // Track this request
        const estimatedInputTokens = usageTracker.estimateInputTokens(options);
        usageTracker.record(estimatedInputTokens);

        const response = await fetch(url, options);

        if (response.status === 429) {
            const stats = usageTracker.getMinuteStats();

            // Parse and log the full quota error details
            try {
                const errorBody = await response.clone().json();
                const errorDetails = errorBody?.error;
                console.error('[Gemini API 429] Rate limit hit:', {
                    model: GEMINI_MODEL,
                    apiKeyPrefix: GEMINI_API_KEY?.substring(0, 8) + '...',
                    errorMessage: errorDetails?.message,
                    errorStatus: errorDetails?.status,
                    quotaDetails: errorDetails?.details,
                    retryAttempt: retryCount + 1,
                    maxRetries: RATE_LIMIT_CONFIG.maxRetries,
                    usage: {
                        requestsInLastMinute: stats.requestsInLastMinute,
                        estimatedInputTokensInLastMinute: stats.inputTokensInLastMinute,
                        totalRequestsThisSession: stats.totalRequestsThisSession,
                        totalEstimatedInputTokensThisSession: stats.totalInputTokensThisSession
                    }
                });
            } catch (parseErr) {
                console.error('[Gemini API 429] Rate limit hit (could not parse response body)', {
                    usage: usageTracker.getMinuteStats()
                });
            }

            if (retryCount >= RATE_LIMIT_CONFIG.maxRetries) {
                throw new Error('Rate limit exceeded after maximum retries');
            }

            // Use Retry-After header if provided, otherwise exponential backoff with jitter
            const retryAfterHeader = response.headers.get('Retry-After');
            let retryDelay;
            if (retryAfterHeader) {
                retryDelay = parseInt(retryAfterHeader, 10) * 1000;
                if (isNaN(retryDelay)) retryDelay = null;
            }
            if (!retryDelay) {
                const baseDelay = Math.min(
                    RATE_LIMIT_CONFIG.initialRetryDelay * Math.pow(2, retryCount),
                    RATE_LIMIT_CONFIG.maxRetryDelay
                );
                // Add random jitter (0-25% of base delay)
                const jitter = Math.random() * baseDelay * 0.25;
                retryDelay = baseDelay + jitter;
            }

            console.log(`Rate limited (429). Retrying in ${(retryDelay / 1000).toFixed(1)}s... (attempt ${retryCount + 1}/${RATE_LIMIT_CONFIG.maxRetries})`);
            await delay(retryDelay);

            return fetchWithRetry(url, options, retryCount + 1);
        }

        return response;
    } catch (error) {
        if (error.message.includes('Rate limit exceeded')) {
            throw error;
        }

        // Network error - retry
        if (retryCount < RATE_LIMIT_CONFIG.maxRetries) {
            const retryDelay = RATE_LIMIT_CONFIG.initialRetryDelay * Math.pow(2, retryCount);
            console.log(`Network error. Retrying in ${retryDelay / 1000} seconds...`);
            await delay(retryDelay);
            return fetchWithRetry(url, options, retryCount + 1);
        }

        throw error;
    }
}

// ============================================================================
// PRODUCT MATCHING UTILITY
// ============================================================================

/**
 * Find a matching product from the catalog based on product name in brief
 * Uses fuzzy matching to handle slight variations in naming
 */
function findMatchingProduct(productName, products) {
    if (!productName || !products || products.length === 0) return null;

    const normalizedName = productName.toLowerCase().trim();

    // Try exact match first
    let match = products.find(p => p.name.toLowerCase().trim() === normalizedName);
    if (match) return match;

    // Try partial match (product name contains or is contained in catalog name)
    match = products.find(p => {
        const catalogName = p.name.toLowerCase().trim();
        return catalogName.includes(normalizedName) || normalizedName.includes(catalogName);
    });
    if (match) return match;

    // Try word overlap matching (for cases like "Premium Widget" vs "Widget Premium")
    const nameWords = normalizedName.split(/\s+/).filter(w => w.length > 2);
    if (nameWords.length > 0) {
        let bestMatch = null;
        let bestScore = 0;

        for (const product of products) {
            const catalogWords = product.name.toLowerCase().split(/\s+/).filter(w => w.length > 2);
            const overlap = nameWords.filter(w => catalogWords.some(cw => cw.includes(w) || w.includes(cw))).length;
            const score = overlap / Math.max(nameWords.length, catalogWords.length);

            if (score > bestScore && score >= 0.5) {
                bestScore = score;
                bestMatch = product;
            }
        }

        if (bestMatch) return bestMatch;
    }

    return null;
}

// ============================================================================
// FUNNEL STAGE CONTEXT (Reused from conversationAgentV2.js)
// ============================================================================

const FUNNEL_STAGE_CONTEXT = {
    TOFU: {
        name: 'Top of Funnel (Awareness)',
        audience: "Cold - they don't know your brand or may not even recognize they have a problem",
        goal: 'Capture attention, create problem awareness, spark curiosity',
        creativeApproach: {
            headlines: 'Problem-focused, curiosity-driven, relatable pain points',
            visuals: 'Broad appeal, emotionally resonant, pattern-interrupting',
            copy: 'Educational, non-salesy, focuses on the problem not the solution',
            tone: 'Approachable, empathetic, curiosity-inducing'
        },
        ctaDirection: ['Learn More', 'Discover', 'See How', 'Find Out'],
        avoid: ['Hard sells', 'Pricing', 'Feature lists', 'Brand-heavy messaging'],
        hookTypes: ['Question hooks', 'Statistic hooks', 'Story hooks', 'Controversy hooks']
    },
    MOFU: {
        name: 'Middle of Funnel (Consideration)',
        audience: 'Warm - they know the problem exists and are evaluating solutions',
        goal: 'Differentiate from competitors, build preference, demonstrate value',
        creativeApproach: {
            headlines: 'Benefit-focused, comparative, outcome-oriented',
            visuals: 'Product in context, social proof, before/after',
            copy: 'Value propositions, differentiators, how it works',
            tone: 'Confident, informative, trust-building'
        },
        ctaDirection: ['See How It Works', 'Compare', 'Get Demo', 'Watch Video'],
        avoid: ['Generic claims', 'Ignoring competitors exist', 'Too much urgency'],
        hookTypes: ['Comparison hooks', 'Transformation hooks', 'Proof hooks', 'Authority hooks']
    },
    BOFU: {
        name: 'Bottom of Funnel (Conversion)',
        audience: 'Hot - they want to buy, just need final push or reassurance',
        goal: 'Convert, overcome final objections, reduce friction',
        creativeApproach: {
            headlines: 'Action-oriented, urgency, specific offers',
            visuals: 'Product focus, testimonials, trust badges, guarantees',
            copy: 'Objection handling, risk reversal, social proof, scarcity',
            tone: 'Direct, confident, reassuring, urgent but not pushy'
        },
        ctaDirection: ['Buy Now', 'Start Free Trial', 'Get Started', 'Claim Offer'],
        avoid: ['Re-explaining the problem', 'Vague benefits', 'Weak CTAs'],
        hookTypes: ['Urgency hooks', 'Social proof hooks', 'Guarantee hooks', 'Scarcity hooks']
    }
};

// ============================================================================
// CONTENT TYPE GUIDANCE
// ============================================================================

const CONTENT_TYPE_GUIDANCE = {
    image: {
        description: 'Single static image with headline and copy',
        bestFor: 'Quick messages, product showcases, quotes, announcements',
        requirements: 'Strong visual, clear headline, minimal text'
    },
    video: {
        description: 'Short-form video content (6-30 seconds) with keyframe-by-keyframe breakdown',
        bestFor: 'Demos, tutorials, behind-the-scenes, storytelling, product reveals',
        requirements: 'Hook in first 2 seconds, keyFrame breakdown, clear narrative arc, strong CTA timing'
    },
    carousel: {
        description: 'Multi-image slideshow (4-6 cards) with a coherent narrative arc',
        bestFor: 'Step-by-step guides, product features, storytelling arcs, transformations',
        requirements: 'Planned story arc, swipe-worthy first card, logical flow, cohesive design',
        storyArcTypes: {
            problem_solution: 'Start with a relatable problem, build tension, reveal the solution',
            before_after: 'Show the transformation journey from struggle to success',
            feature_showcase: 'Highlight different features/benefits across cards',
            educational_steps: 'Teach something valuable step-by-step',
            testimonial_journey: 'Build social proof through customer experience'
        }
    }
};

// ============================================================================
// A/B TEST TYPES
// ============================================================================

const AB_TEST_TYPES = {
    same_visual_different_text: {
        name: 'Same Visual, Different Text',
        description: 'Test headline, CTA, or copy variations while keeping the visual constant',
        effort: 'low',
        testableElements: ['headline', 'cta', 'primaryText', 'tone'],
        typicalLearning: 'Message resonance, copy effectiveness'
    },
    completely_different: {
        name: 'Completely Different Creatives',
        description: 'Test two entirely different creative approaches',
        effort: 'high',
        testableElements: ['angle', 'visual', 'concept'],
        typicalLearning: 'Creative direction, audience preferences'
    }
};

// ============================================================================
// AI BULK GENERATION
// ============================================================================

/**
 * Build comprehensive brand context for brief generation
 * Includes all brand information from the brands page
 */
function buildBrandContextForBriefs(brand) {
    if (!brand) return '**Brand:** Unknown Brand';

    const sections = [];

    // Brand identity
    sections.push(`**Brand Name:** ${brand.name || 'Unknown'}`);
    if (brand.industry) sections.push(`**Industry:** ${brand.industry}`);
    if (brand.products) sections.push(`**Products/Services:** ${brand.products}`);
    if (brand.website) sections.push(`**Website:** ${brand.website}`);

    // Brand voice
    if (brand.voice?.tone) sections.push(`**Brand Tone:** ${brand.voice.tone}`);
    if (brand.voice?.personality) sections.push(`**Brand Personality:** ${brand.voice.personality}`);

    // Target audience
    if (brand.audience?.demographics) sections.push(`**Target Demographics:** ${brand.audience.demographics}`);
    if (brand.audience?.interests) sections.push(`**Audience Interests:** ${brand.audience.interests}`);
    if (brand.audience?.painPoints) sections.push(`**Audience Pain Points:** ${brand.audience.painPoints}`);

    // Visual identity - colors
    if (brand.visual?.primaryColor || brand.visual?.secondaryColor || brand.visual?.accentColor) {
        const colors = [];
        if (brand.visual.primaryColor) colors.push(`Primary: ${brand.visual.primaryColor}`);
        if (brand.visual.secondaryColor) colors.push(`Secondary: ${brand.visual.secondaryColor}`);
        if (brand.visual.accentColor) colors.push(`Accent: ${brand.visual.accentColor}`);
        sections.push(`**Brand Color Palette:** ${colors.join(', ')}`);
        sections.push(`  - Use these colors in sceneDescriptions for backgrounds, props, lighting, and environmental elements`);
        sections.push(`  - The brand palette should be visually evident in every creative`);
    }

    // Typography
    if (brand.visual?.fonts?.heading) sections.push(`**Heading Font:** ${brand.visual.fonts.heading}`);
    if (brand.visual?.fonts?.body) sections.push(`**Body Font:** ${brand.visual.fonts.body}`);

    return sections.join('\n');
}

/**
 * Build the bulk brief generation prompt
 */
function buildBulkBriefPrompt(promptConfig) {
    const { brand, pillar, counts, existingBriefs = [], config = {}, products = [], contentTypeCounts = null, skipProductContext = false, pillarProductAssignments = [] } = promptConfig;

    // Build comprehensive brand context
    const brandContext = buildBrandContextForBriefs(brand);

    const stageContexts = Object.entries(counts)
        .filter(([, count]) => count > 0)
        .map(([stage, count]) => {
            const ctx = FUNNEL_STAGE_CONTEXT[stage];
            return `
### ${stage} (${count} briefs needed)
**Audience:** ${ctx.audience}
**Goal:** ${ctx.goal}
**Headlines:** ${ctx.creativeApproach.headlines}
**Visuals:** ${ctx.creativeApproach.visuals}
**Copy:** ${ctx.creativeApproach.copy}
**Tone:** ${ctx.creativeApproach.tone}
**CTAs:** ${ctx.ctaDirection.join(', ')}
**Hook Types to Use:** ${ctx.hookTypes.join(', ')}
**Avoid:** ${ctx.avoid.join(', ')}
`;
        }).join('\n');

    // Build product context if products are available
    // If we have per-slot product assignments, build a detailed assignment section
    // Otherwise, use the general product context
    let productContext = '';
    let productAssignmentNote = '';

    if (pillarProductAssignments && pillarProductAssignments.length > 0 && products && products.length > 0) {
        // Build per-slot product assignment context
        // Group assignments by funnel stage for clarity
        const stageGroups = { TOFU: [], MOFU: [], BOFU: [] };

        pillarProductAssignments.forEach(assignment => {
            const product = products.find(p => p.id === assignment.productId);
            if (product && stageGroups[assignment.funnelStage]) {
                stageGroups[assignment.funnelStage].push({
                    slotIndex: assignment.slotIndex,
                    product
                });
            }
        });

        productAssignmentNote = `
## PRE-ASSIGNED PRODUCTS FOR EACH BRIEF SLOT
⚠️ CRITICAL: Each brief slot has a SPECIFIC product pre-assigned. You MUST feature the exact product specified for each slot.
The product MUST be deeply integrated into the brief:
- The headline MUST reference or relate to the assigned product
- The marketingAngle MUST highlight the product's unique value
- The sceneDescription MUST prominently feature the product by name and describe where it sits in the scene (but do NOT describe the product's visual appearance — reference images will be provided during generation)
- The caption MUST mention the product by name
- For carousels, the product should appear across multiple cards

${Object.entries(stageGroups).filter(([, slots]) => slots.length > 0).map(([stage, slots]) => `
### ${stage} Briefs (${slots.length} total)
${slots.map((slot, idx) => {
    const p = slot.product;
    const allImages = p.images?.length > 0
        ? p.images.map(img => typeof img === 'string' ? img : img.url).filter(Boolean)
        : (p.imageUrl ? [p.imageUrl] : []);
    return `
**Brief ${idx + 1} (${stage})** → MUST feature: "${p.name}"
${p.description ? `- Description: ${p.description}` : ''}
${p.visualNotes ? `- VISUAL APPEARANCE (use this for accurate image generation): ${p.visualNotes}` : ''}
${p.price ? `- Price: ${p.price}` : ''}
${p.features?.length > 0 ? `- Key Features: ${p.features.join(', ')}` : ''}
${p.productUrl ? `- Product URL (for CTAs): ${p.productUrl}` : ''}
${allImages.length > 0 ? `- Has ${allImages.length} reference image(s) available` : ''}
`;
}).join('')}`).join('\n')}

INTEGRATION REQUIREMENTS:
1. The "product" field in your JSON output MUST exactly match the assigned product name
2. Headlines should naturally incorporate the product or its key benefit
3. sceneDescription MUST mention the product by name and describe its placement in the scene, but do NOT describe the product's visual appearance (colors, labels, shape, packaging) — product reference images will be provided during generation and must be reproduced exactly from those references
4. For BOFU: use the product URL as the productPageUrl for paid CTAs
5. Never swap products between slots - each slot must use ONLY its assigned product
`;
    } else if (!skipProductContext && products && products.length > 0) {
        const linkedProductIds = config.activePromotion?.linkedProducts || [];
        const productContextOptions = linkedProductIds.length > 0
            ? { productIds: linkedProductIds, maxProducts: 0 } // Use all linked products
            : { maxProducts: 3 }; // Randomly select up to 3 products
        productContext = buildProductContext(products, productContextOptions);
    } else if (skipProductContext) {
        productAssignmentNote = `
## PRODUCT ASSIGNMENT
**NOTE:** Specific products will be assigned to each brief after generation. Generate content focused on the pillar theme and funnel stage. Use placeholders like "[PRODUCT]" or "[PRODUCT NAME]" where you would reference a specific product. The actual product details will be filled in during the review step.
`;
    }

    return `You are a senior creative strategist generating content briefs for a monthly content plan. You are generating briefs EXCLUSIVELY for the "${pillar.name}" marketing pillar. Every brief you produce must be clearly and directly about this pillar's theme.

## BRAND CONTEXT
${brandContext}
${productContext ? `
${productContext}
` : ''}${productAssignmentNote}
## MARKETING PILLAR — ALL BRIEFS MUST BE ABOUT THIS PILLAR
**Pillar:** ${pillar.name}
**Description:** ${pillar.description || 'Not specified'}
**Tone of Voice:** ${pillar.contentGuidelines?.toneOfVoice?.join(', ') || 'Consistent with brand'}
**Key Messages:** ${pillar.contentGuidelines?.keyMessages?.join(', ') || 'Brand-aligned messaging'}
**Example Topics:** ${pillar.contentGuidelines?.exampleTopics?.join(', ') || 'Relevant to pillar theme'}

⚠️ PILLAR ADHERENCE (MANDATORY):
- The pillar "${pillar.name}" is the CENTRAL THEME for every brief you generate in this batch.
- Every brief's headline, marketingAngle, caption, hookDescription, objective, and sceneDescription MUST directly relate to "${pillar.name}"${pillar.description ? ` (${pillar.description})` : ''}.
- Use the Key Messages listed above as the foundation for your marketing angles and headlines. Each brief should reinforce at least one of these key messages.
- Use the Example Topics listed above as starting points for brief topics. Every brief must fall within the scope of these topics or closely related themes.
- The Tone of Voice listed above MUST be reflected in every caption, headline, and hook.
- Do NOT generate generic brand content that could belong to any pillar. Every brief must be unmistakably about "${pillar.name}".
- If you cannot connect a brief idea to this pillar's theme, description, or key messages — discard it and generate one that does.

## FUNNEL STAGE REQUIREMENTS
${stageContexts}

## OUTPUT REQUIREMENTS
Generate content briefs as a JSON array. For each brief, provide:

{
  "funnelStage": "TOFU" | "MOFU" | "BOFU",
  "outputType": "image" | "video" | "carousel",
  "product": "specific product or offer to feature (MUST match a product name from the list above if products are provided)",
  "productPageUrl": "landing page URL for paid content CTAs (use the product's URL if available)",
  "targetAudience": "specific audience segment",
  "objective": "what this creative should achieve",
  "marketingAngle": "unique angle or hook for this creative",
  "desiredEmotion": "primary emotion to evoke",
  "selectedHeadline": "compelling headline (under 10 words)",
  "cta": "clear call-to-action",
  "tone": "specific tone for this piece",
  "visualStyle": "description of visual approach",
  "sceneDescription": "EXPLICIT image generation prompt (50-100 words). MUST describe: (1) exact visual scene composition and setting, (2) subject positioning, poses, and expressions, (3) lighting direction, mood, and color palette using the brand colors, (4) product placement and size. Do NOT include text overlay instructions (headline, CTA, font styles, or colors) — those are handled separately. Mention that the brand logo should be included (typically bottom corner) but do NOT describe the logo's colors, design, or appearance — the logo will be provided as a reference image. Similarly, do NOT describe the product's visual details (color, shape, labels) — the product's appearance will come from reference images. Focus on the SCENE, SETTING, and COMPOSITION around the product.",
  "hookDescription": "the specific hook used in this creative",
  "caption": "engaging social media caption (2-4 sentences) that complements the visual",
  "hashtags": ["relevant", "hashtags", "without", "the", "symbol", "5-8 total"]
}

### CAROUSEL-SPECIFIC REQUIREMENTS
For briefs with outputType "carousel", you MUST follow a two-step process:

**STEP 1: Plan the Story Arc Based on Pillar**
Choose the story arc that best matches the PILLAR you are generating content for. Do NOT default to "problem_solution" for every carousel.

STORY ARCS (choose based on pillar):
NEGATIVE/PROBLEM START (use sparingly, only when pillar calls for it):
- "problem_solution": Problem -> Solution (starts with pain point, introduces solution)
- "before_after": Before -> After Transformation (starts with struggle, shows transformation)
- "testimonial_journey": Testimonial Journey (starts with customer skepticism, shows results)

POSITIVE START (preferred for Entertainment, Behind the Scenes, Offers):
- "aspiration_journey": Aspiration Journey - Start with the DREAM outcome, show how to achieve it
- "day_in_life": Day in the Life - Start with a relatable positive moment, product enhances it
- "community_celebration": Community Celebration - Start with vibrant community, invite viewer to join

NEUTRAL START (great for Education, Product Features):
- "curiosity_reveal": Curiosity & Reveal - Spark intrigue with a teaser, reveal the answer
- "behind_the_magic": Behind the Magic - Show impressive result first, reveal the craftsmanship behind it
- "feature_showcase": Feature Showcase - Tease value, spotlight features, show complete package
- "educational_steps": Educational Steps - State the question, teach the process, show results

PILLAR-ARC RECOMMENDATIONS:
- Education pillar → Use "educational_steps" or "curiosity_reveal"
- Entertainment pillar → Use "day_in_life" or "curiosity_reveal" (NEVER start negative)
- Social Proof pillar → Use "testimonial_journey" or "community_celebration"
- Product Features pillar → Use "feature_showcase" or "behind_the_magic"
- Behind the Scenes pillar → Use "behind_the_magic" or "day_in_life" (avoid "problem_solution")
- Offers & Promotions pillar → Use "aspiration_journey" (show the dream, not the pain)

**STEP 2: Generate Per-Card Details Following the Arc**
Each card's sceneDescription must be a COMPLETE image generation prompt (50-100 words) that describes the exact visual scene, setting, composition, lighting, mood, color palette, subject positioning, and key visual elements. Do NOT include text overlay instructions in the sceneDescription — headline, CTA, font styles, and colors are handled separately during image generation. IMPORTANT: the VISUAL WORLD described must be consistent across all cards — same subject/model, same or evolving environment, same lighting style, same color palette. Think of each card as a different frame from the same photoshoot, not a separate shoot.

CRITICAL — CARD UNIQUENESS: Each card's sceneDescription MUST describe a COMPLETELY DIFFERENT scene — different setting OR composition OR subjects OR camera angle. Cards that describe the same visual with minor wording differences are NOT acceptable. The headline on each card MUST advance the story — no repetition of themes across cards. If two cards look like they could produce the same image, rewrite one of them.

Include "storyArcType" and "storyNarrative" fields on the brief, and "cardRole" on each card:

{
  "outputType": "carousel",
  "storyArcType": "problem_solution" | "before_after" | "feature_showcase" | "educational_steps" | "testimonial_journey" | "aspiration_journey" | "curiosity_reveal" | "day_in_life" | "behind_the_magic" | "community_celebration",
  "storyNarrative": "2-3 sentence description of the overall carousel story. What journey does the viewer take from card 1 to the final card?",
  "visualSeed": {
    "protagonist": "Detailed description of the main character/subject that appears across ALL cards. Include: gender, approximate age, ethnicity/skin tone, hair color and style, clothing description, and any distinguishing features. Be specific enough that the same person can be recognized across all cards. Example: 'A woman in her early 30s with shoulder-length wavy dark brown hair, warm olive skin, wearing a casual white linen blouse and small gold hoop earrings. She has a friendly, approachable expression with subtle smile lines.'",
    "settingProgression": "Describe how the environment evolves across the carousel to serve the story. Settings CAN change between cards if it advances the narrative. Format as array with one entry per card. Example for problem_solution arc: ['Card 1: Cramped, cluttered bathroom with harsh fluorescent lighting and products scattered everywhere', 'Card 2: Same bathroom but tidier, the product prominently visible on the counter', 'Card 3: Bright, salon-quality lighting revealing a clean modern space', 'Card 4: Confident subject ready to leave, natural daylight streaming in']",
    "visualMood": "The consistent photographic style and mood across all cards. Include: lighting style (soft, dramatic, natural, studio), color grading (warm, cool, neutral, muted), and photography aesthetic (editorial, lifestyle, product-focused, candid). Example: 'Warm, golden-hour inspired lighting with soft diffused shadows, lifestyle photography aesthetic, slightly warm color grading with emphasis on natural skin tones.'"
  },
  "carouselCards": [
    {
      "cardPosition": 1,
      "cardRole": "hook",
      "hookType": "question|curiosity|transformation|shock|statistic",
      "headline": "Short punchy headline for this card (max 8 words)",
      "caption": "Card 1 caption (1-2 sentences). Open the story: state a specific pain point or provocative question that the target audience deeply relates to. Name the situation concretely — no vague hooks. This caption sets up a narrative thread that cards 2-4 MUST continue. Write in brand voice.",
      "sceneDescription": "COMPLETE image prompt (50-100 words): Describe the exact visual scene for card 1. Include composition, lighting, subjects, mood, colors, and setting. Do NOT include text overlay instructions (headline, CTA, font styles) — those are added separately. For problem_solution: show the PROBLEM vividly. Must stop the scroll. Do NOT include the brand logo on this card - the hook card should feel organic and curiosity-driven, not branded. Do NOT describe the product's visual appearance (colors, labels, shape) — product reference images will be provided separately."
    },
    {
      "cardPosition": 2,
      "cardRole": "build",
      "headline": "Headline that continues the story",
      "caption": "Card 2 caption (1-2 sentences). DIRECTLY continue the story from card 1's caption — reference the same situation or question, then introduce the product BY NAME as the answer. The reader should feel this is the next sentence in the same story, not a new topic.",
      "sceneDescription": "COMPLETE image prompt (50-100 words): Describe the exact visual scene for card 2. Do NOT include text overlay instructions — those are added separately. For problem_solution: introduce the solution. Visually distinct from card 1 while maintaining cohesive style. Brand logo optional - include only if it fits the composition. Do NOT describe the product's or logo's visual appearance — reference images will be provided separately."
    },
    {
      "cardPosition": 3,
      "cardRole": "proof",
      "headline": "Headline that builds credibility",
      "caption": "Card 3 caption (1-2 sentences). Continue the SAME narrative thread from cards 1-2. Provide specific proof that the product solves the problem stated in card 1 — use numbers, features, or concrete results. The reader must feel the story progressing, not starting over.",
      "sceneDescription": "COMPLETE image prompt (50-100 words): Describe the exact visual scene for card 3. Do NOT include text overlay instructions — those are added separately. Show evidence, transformation in progress, or feature detail. Visually distinct from previous cards. Brand logo optional - include only if it fits the composition. Do NOT describe the product's or logo's visual appearance — reference images will be provided separately."
    },
    {
      "cardPosition": 4,
      "cardRole": "cta",
      "headline": "Action-oriented headline that invites the viewer to take action",
      "ctaButtonText": "Short action phrase for the CTA button (2-4 words). MUST be a direct action verb phrase like 'Shop Now', 'Get Started', 'Try It Free', 'Join Today'. MUST match the mood of the headline - if headline is aspirational, CTA should be aspirational. NEVER use generic CTAs like 'Learn More' or 'See How It Works' unless the carousel is educational.",
      "caption": "Final caption (1-2 sentences). Close the story arc opened in card 1 — refer back to the original problem/question and show it's now resolved. End with a specific CTA telling them exactly what to do next. The reader should feel the narrative has come full circle.",
      "sceneDescription": "COMPLETE image prompt (50-100 words): Describe the exact visual scene for the final card. Do NOT include text overlay instructions — those are added separately. MUST include the brand logo (bottom corner or center) but do NOT describe the logo's colors or design — it will be provided as a reference image. Show the resolution, outcome, or call-to-action scene. Must feel like a satisfying conclusion. Do NOT describe the product's visual appearance — reference images will be provided separately."
    }
  ]
}

CAROUSEL STORYTELLING RULES:
- FIRST choose the storyArcType based on the funnel stage and marketing angle
- THEN plan the storyNarrative - the complete journey from card 1 to the last card
- Card 1 MUST have a strong hook that stops the scroll (specify hookType)
- Each card MUST have a cardRole: "hook", "build", "proof", "cta", or "support"
- Each card MUST have a UNIQUE, DETAILED sceneDescription (50-100 words) that works as a self-contained image generation prompt
- Each card MUST have a UNIQUE caption that tells the card's part of the story — captions must form ONE continuous narrative, not independent taglines
- Cards must follow the chosen story arc logically - each card's caption MUST reference or continue from the previous card's content
- HEADLINE REQUIREMENTS:
  * Card 1 headline MUST be a strong hook (question, curiosity, bold statement, surprising fact, or relatable pain point) that stops the scroll
  * Cards 2-4 headlines MUST follow up coherently on the hook - they should build on the story, not be random taglines
  * Headlines should feel connected when read in sequence - if they could be randomly reordered without losing meaning, rewrite them
  * The final card headline should drive toward action and connect back to the hook
- VISUAL CONTINUITY: All cards' sceneDescriptions MUST describe scenes from the SAME visual world. Same subject/model across all cards (same clothing, hair, features). Same or evolving environment (same room or setting — not jumping between unrelated locations). Lighting mood should evolve with the story arc (e.g., moodier for the problem → brighter for the solution). Same brand color integration approach. The viewer must feel they are watching one continuous story unfold, not separate photoshoots.
- Visual style should be COHESIVE across cards (same color palette, consistent lighting mood)
- Final card should drive action with clear CTA energy
⚠️ CTA RULES — CRITICAL (READ BEFORE WRITING CARD 4):

THE CTA MUST COMPLETE THE STORY. Before writing the CTA, re-read the headlines and captions from Cards 1-4. The CTA should feel like the natural next step in THAT specific story.

STORY-TO-CTA ALIGNMENT PROCESS:
1. What is the EMOTIONAL JOURNEY of this carousel? (educational, aspirational, problem-solving, lifestyle, etc.)
2. What would a viewer who just experienced this story WANT to do next?
3. Write a CTA that answers that desire.

EXAMPLES:
- Story about family cooking memories → CTA: "Start Your Tradition" or "Gather Your Family"
- Story teaching how a product works → CTA: "See How It Works" or "Try It Yourself"
- Story about solving a frustrating problem → CTA: "Fix It Now" or "Get Relief Today"
- Story about aspiration/legacy → CTA: "Begin Your Journey" or "Start Today"

WRONG: Emotional family story + "See How It Works" (mismatched - educational CTA on emotional story)
WRONG: Educational how-to story + "Create Lasting Memories" (mismatched - emotional CTA on educational story)
RIGHT: The CTA tone matches the story tone.

CTA CONSISTENCY: The ctaButtonText field is used directly as the CTA text overlay during image generation. Ensure it matches the mood and tone of the headline.
- Generate 4-6 cards depending on story complexity
- Do NOT use generic descriptions like "attention-grabbing scene" - be SPECIFIC about what is in the image
- LOGO RULES: Card 1 (hook) must NOT include the brand logo. Middle cards may optionally include the logo if it fits naturally. The FINAL card MUST include the brand logo prominently. When mentioning the logo, just say "include the brand logo" — do NOT describe the logo's colors, design, or appearance (it will be provided as a reference image during generation).
- TEXT OVERLAY RULES: Do NOT include text overlay instructions (headline, CTA, font styles, colors) in the sceneDescription. Text overlays are added separately during image generation using the headline and ctaButtonText fields. Focus the sceneDescription purely on the visual scene.
- PRODUCT & LOGO APPEARANCE: Do NOT describe the product's visual details (colors, labels, shape, packaging) or the logo's colors/design in the sceneDescription. Product and logo reference images will be provided during generation — focus the scene description on the SETTING, COMPOSITION, and ATMOSPHERE. Just mention the product by name and where it should be placed in the scene.

CAPTION WRITING RULES (CRITICAL - follow these for EVERY card caption):

NARRATIVE COHERENCE IS THE #1 PRIORITY. The captions across all cards must read as ONE continuous story, not as separate taglines. Before writing individual card captions, mentally compose a single 4-6 sentence narrative paragraph that tells the complete story. Then split that paragraph across the cards — each card gets the next sentence(s) in the story.

TEST: Read all captions back-to-back. If they sound like they were written independently or could be rearranged without losing meaning, they FAIL. Rewrite them until they form an obvious narrative sequence where each caption DEPENDS on the one before it.

GOOD EXAMPLE (coherent story):
- Card 1: "You spend 45 minutes every morning wrestling with a flat iron that never gets the temperature right."
- Card 2: "GlossWave's ceramic plates heat to your exact setting in 12 seconds — and hold it within 1 degree for the entire session."
- Card 3: "87% of stylists who switched to GlossWave cut their styling time in half. The dual-sensor technology means zero hot spots, zero damage."
- Card 4: "Take back your mornings. Try GlossWave risk-free for 30 days — your hair will thank you."

BAD EXAMPLE (disconnected taglines — DO NOT DO THIS):
- Card 1: "Styling stress got you down?"
- Card 2: "GlossWave transforms your routine"
- Card 3: "Seamless styling. Deeper confidence."
- Card 4: "Start your hair journey today."

BAD CTA EXAMPLE (mismatched tone — DO NOT DO THIS):
- Headline: "Start your cooking legacy." (aspirational, emotional)
- ctaButtonText: "See How It Works" (educational, doesn't match)
This is WRONG. The CTA should match the headline mood.

GOOD CTA EXAMPLE:
- Headline: "Start your cooking legacy." (aspirational, emotional)
- ctaButtonText: "Shop the Bundle" or "Begin Your Legacy" (matches the aspirational tone)

Rules:
- Each caption MUST explicitly connect to the previous card's content through shared subjects, pronouns, or continued narrative
- NEVER use generic filler phrases: "swipe to discover", "something's missing", "see the difference", "delivers where others fall short", "changed everything", "tap the link"
- ALWAYS reference the SPECIFIC product by name and mention SPECIFIC features, benefits, or outcomes
- ALWAYS write in the brand's tone of voice (${brand?.brandVoice || 'professional and engaging'})
- Use power words and sensory language that create emotion and mental imagery
- Card 1: Open a loop — state a SPECIFIC, concrete problem the audience faces (not a vague question)
- Card 2: Introduce the product BY NAME as the direct answer to card 1's problem — explain the specific mechanism
- Middle cards: Provide concrete proof (numbers, features, results) that continues the same narrative thread
- Final card: Close the loop by referencing card 1's problem as now solved, then give a specific CTA

### VIDEO-SPECIFIC REQUIREMENTS
For briefs with outputType "video", you MUST provide additional video-specific fields that describe the TEMPORAL structure of the ad. Videos are NOT static images — they unfold over time and need a keyframe-by-keyframe breakdown.

{
  "outputType": "video",
  "videoLength": "6s" | "15s" | "30s",
  "videoStyle": "live-action" | "motion-graphics" | "animated" | "product-demo" | "testimonial",
  "videoNarrative": "2-3 sentence description of the video's complete story from opening hook to final CTA. What does the viewer SEE and FEEL across the duration?",
  "keyFrames": [
    {
      "timeCode": "0-2s",
      "visual": "Exact description of what's on screen — scene, composition, subject, movement, camera angle, lighting",
      "textOverlay": "Any text displayed at this point (headline, stat, question) with font style and color",
      "audio": "Music mood, voiceover text, or sound effect"
    },
    {
      "timeCode": "2-4s",
      "visual": "Next scene or transition — what changes, what moves, what appears",
      "textOverlay": "Text on screen at this point",
      "audio": "Audio direction"
    }
  ],
  "musicMood": "upbeat" | "dramatic" | "ambient" | "energetic" | "emotional",
  "ctaTiming": "When the CTA appears (e.g. 'last 2 seconds')"
}

Provide enough keyFrames to cover the entire video length (e.g., 3 keyFrames for 6s, 5-6 for 15s, 8-10 for 30s).

VIDEO STORYTELLING RULES:
- The HOOK must appear in the first 2 seconds — a visual or text that stops the scroll
- Each keyFrame's "visual" must describe a SPECIFIC scene, not vague directions like "engaging visuals"
- The video must have a clear narrative arc: hook → build → payoff/CTA
- "sceneDescription" for videos describes the OVERALL visual world and setting (used for thumbnail/preview)
- "keyFrames" describe the TEMPORAL progression — what happens moment by moment
- Brand logo MUST appear in the final keyFrame
- Text overlays in keyFrames must specify the exact text, font style, and color
- Each keyFrame visual must be distinct — different camera angle, subject position, or composition
- The video must feel like a STORY, not a slideshow of similar shots

## CONTENT TYPE DISTRIBUTION
${contentTypeCounts ? `You MUST generate EXACTLY this many briefs of each content type:
- Images (static): EXACTLY ${contentTypeCounts.image} briefs with outputType "image"
- Videos: EXACTLY ${contentTypeCounts.video} briefs with outputType "video"
- Carousels: EXACTLY ${contentTypeCounts.carousel} briefs with outputType "carousel"

This is a hard requirement, not a suggestion. The total must equal ${contentTypeCounts.image + contentTypeCounts.video + contentTypeCounts.carousel} briefs.` : `Use this approximate distribution when assigning outputType:
- Images (static): ${config.contentTypeDistribution?.image || 50}%
- Videos: ${config.contentTypeDistribution?.video || 30}%
- Carousels: ${config.contentTypeDistribution?.carousel || 20}%`}

## CRITICAL PROMOTIONAL CONTENT RULES
${config.activePromotion ? `
This content is being generated for an ACTIVE PROMOTION. Include promotional messaging as specified below.
${buildPromotionContext(config.activePromotion, products)}
` : `
NEVER include any of the following in briefs:
- Sales, discounts, or percentage off messaging
- Promo codes or coupon references
- "Limited time offer" or urgency language around pricing
- "Buy one get one" or bundle deals
- Free shipping mentions
- "Save $X" or price reduction messaging
- Bonus offers or giveaways

This content should focus on brand value, education, and engagement - NOT sales.
Content must build brand awareness and trust WITHOUT promotional messaging.
`}

## IMPORTANT RULES
1. **PILLAR FIRST**: Every brief MUST be directly about the "${pillar.name}" pillar. The headline, marketing angle, caption, and scene must all clearly connect to this pillar's theme and key messages. A brief that could belong to a different pillar is WRONG — rewrite it.
2. Each brief MUST be UNIQUE - vary angles, hooks, and approaches WITHIN the pillar theme
3. Headlines should be punchy and under 10 words, and must reference the pillar's subject matter
4. TOFU content must NOT mention pricing or hard sells
5. BOFU content must include urgency or social proof elements${config.activePromotion ? ' (can reference the active promotion)' : ' (NO discounts or sale messaging)'}
6. Follow the content type distribution above when assigning outputType
7. Ensure variety in marketing angles - don't repeat the same approach, but ALL angles must be rooted in the "${pillar.name}" pillar
8. Scene descriptions MUST be visual-only image generation prompts. Describe: product placement within the scene, subject positioning, lighting, mood, brand colors, and logo placement. Do NOT include text overlay instructions (headline, CTA, font styles, colors) — those are added separately during image generation. Do NOT describe the product's visual appearance (colors, labels, shape, packaging) or the logo's colors/design — product and logo reference images will be provided separately. Focus on the SETTING, COMPOSITION, and ATMOSPHERE around the product
9. ${config.activePromotion ? 'INCLUDE the promotional details in relevant BOFU content' : 'NEVER mention sales, discounts, or promotions'}
${(() => {
    const hasLinkedProducts = config.activePromotion?.linkedProducts?.length > 0;
    const linkedProductNames = hasLinkedProducts
        ? products.filter(p => config.activePromotion.linkedProducts.includes(p.id)).map(p => p.name)
        : [];
    if (hasLinkedProducts && linkedProductNames.length > 0) {
        return `10. **CRITICAL - PROMOTION LINKED PRODUCTS**: This promotion has specific linked products that MUST be featured:
   - Linked products: ${linkedProductNames.join(', ')}
   - EVERY brief MUST reference at least one of these linked products by EXACT name
   - Do NOT reference products outside this list for promotional content`;
    } else if (products && products.length > 0) {
        return `10. **PRODUCT REFERENCES**: Reference products from the catalog when appropriate. Use EXACT product names from the list above.`;
    } else {
        return `10. Focus on brand-level messaging since no specific products are linked.`;
    }
})()}

${existingBriefs.length > 0 ? `
## EXISTING BRIEFS TO DIFFERENTIATE FROM
The following briefs have already been created. Ensure your new briefs are distinctly different:
${existingBriefs.map(b => `- ${b.funnelStage}: "${b.selectedHeadline}" (${b.marketingAngle})`).join('\n')}
` : ''}

FINAL REMINDER: Every brief in your response must be unmistakably about the "${pillar.name}" pillar. Re-read the pillar description, key messages, and example topics above. If any brief does not clearly connect to this pillar — rewrite it before responding.

Return ONLY a valid JSON array of briefs. No markdown, no explanation, just the JSON array.

${COPY_STYLE_GUIDE}`;
}

/**
 * Attempt to repair truncated JSON from a maxOutputTokens cutoff.
 * Tries to close any open arrays/objects so JSON.parse succeeds.
 */
function repairTruncatedJson(text) {
    // If it already parses, no repair needed
    try {
        JSON.parse(text);
        return text;
    } catch (e) {
        // Continue with repair
    }

    // Remove any trailing incomplete string value (cut mid-string)
    let repaired = text.replace(/,\s*"[^"]*$/, '');
    // Remove trailing incomplete key-value pairs
    repaired = repaired.replace(/,\s*"[^"]*"\s*:\s*("[^"]*)?$/, '');
    // Remove trailing comma
    repaired = repaired.replace(/,\s*$/, '');

    // Count open brackets/braces and close them
    let openBraces = 0;
    let openBrackets = 0;
    let inString = false;
    let escaped = false;
    for (const ch of repaired) {
        if (escaped) { escaped = false; continue; }
        if (ch === '\\') { escaped = true; continue; }
        if (ch === '"') { inString = !inString; continue; }
        if (inString) continue;
        if (ch === '{') openBraces++;
        if (ch === '}') openBraces--;
        if (ch === '[') openBrackets++;
        if (ch === ']') openBrackets--;
    }

    // Close any open structures
    while (openBraces > 0) { repaired += '}'; openBraces--; }
    while (openBrackets > 0) { repaired += ']'; openBrackets--; }

    console.warn('[JSON Repair] Attempted to repair truncated JSON. Open braces/brackets closed.');
    return repaired;
}

/**
 * Merge orphaned carousel fragments back into their parent briefs.
 *
 * The AI sometimes generates carousel briefs as split objects:
 * - Object A: has all base fields (headline, angle, etc.) + outputType "carousel" but NO carouselCards
 * - Object B: has carouselCards, storyArcType, storyNarrative but few/no base fields
 *
 * This function detects and merges them.
 */
function mergeOrphanedCarouselFragments(briefs) {
    if (!Array.isArray(briefs) || briefs.length < 2) return briefs;

    // Identify carousel briefs missing carouselCards and orphaned fragments
    const completeBriefs = [];
    const incompleteCarousels = []; // carousel briefs without carouselCards
    const orphanedFragments = []; // objects with carouselCards but missing key base fields

    for (const brief of briefs) {
        const isCarousel = brief.outputType === 'carousel';
        const hasCards = Array.isArray(brief.carouselCards) && brief.carouselCards.length > 0;
        const hasBaseFields = brief.selectedHeadline || brief.headline || brief.marketingAngle;

        if (isCarousel && hasCards && hasBaseFields) {
            // Complete carousel brief — keep as-is
            completeBriefs.push(brief);
        } else if (isCarousel && !hasCards && hasBaseFields) {
            // Carousel brief missing its cards
            incompleteCarousels.push(brief);
        } else if (hasCards && !hasBaseFields) {
            // Orphaned fragment with only carousel data
            orphanedFragments.push(brief);
        } else {
            // Non-carousel or other brief
            completeBriefs.push(brief);
        }
    }

    // Try to match orphaned fragments to incomplete carousels
    for (const incomplete of incompleteCarousels) {
        if (orphanedFragments.length > 0) {
            const fragment = orphanedFragments.shift();
            // Merge: incomplete brief gets the carousel data from the fragment
            const merged = {
                ...incomplete,
                carouselCards: fragment.carouselCards,
                storyArcType: fragment.storyArcType || incomplete.storyArcType,
                storyNarrative: fragment.storyNarrative || incomplete.storyNarrative
            };
            console.log('[Brief Merge] Merged orphaned carousel fragment into incomplete brief:', merged.selectedHeadline || merged.headline);
            completeBriefs.push(merged);
        } else {
            // No fragment to merge — keep as-is
            console.warn('[Brief Merge] Carousel brief missing carouselCards with no available fragment to merge:', incomplete.selectedHeadline || incomplete.headline);
            completeBriefs.push(incomplete);
        }
    }

    // Any remaining orphaned fragments that couldn't be matched — discard them
    for (const fragment of orphanedFragments) {
        console.warn('[Brief Merge] Discarding unmatched carousel fragment with', fragment.carouselCards?.length, 'cards');
    }

    return completeBriefs;
}

/**
 * Extract brand color palette from brand object
 */
function getBrandColorPalette(brand) {
    if (!brand?.visual) return null;

    const colors = {
        primary: brand.visual.primaryColor || null,
        secondary: brand.visual.secondaryColor || null,
        accent: brand.visual.accentColor || null
    };

    // Only return if at least one color is defined
    if (colors.primary || colors.secondary || colors.accent) {
        return colors;
    }
    return null;
}

/**
 * Generate briefs for a single pillar
 */
async function generatePillarBriefs(brand, pillar, counts, existingBriefs = [], config = {}, products = [], contentTypeCounts = null, skipProductContext = false, pillarProductAssignments = []) {
    const prompt = buildBulkBriefPrompt({ brand, pillar, counts, existingBriefs, config, products, contentTypeCounts, skipProductContext, pillarProductAssignments });

    // Extract brand colors to attach to each brief
    const brandColors = getBrandColorPalette(brand);

    // Debug: Log the full brief generation prompt
    console.log('='.repeat(80));
    console.log('📝 [BRIEF GENERATION PROMPT] Pillar:', pillar.name);
    console.log('='.repeat(80));
    console.log(prompt);
    console.log('='.repeat(80));

    const response = await fetchWithRetry(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            generationConfig: {
                temperature: 0.8,
                topK: 40,
                topP: 0.95,
                maxOutputTokens: 32768
            }
        })
    });

    if (!response.ok) {
        const error = await response.text();
        console.error('Gemini API error:', error);
        throw new Error(`Failed to generate briefs: ${response.status}`);
    }

    const data = await response.json();
    const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
    const finishReason = data.candidates?.[0]?.finishReason;

    if (finishReason === 'MAX_TOKENS') {
        console.warn('[Gemini API] Response was truncated due to maxOutputTokens limit. Carousel briefs may be incomplete.');
    }

    if (!text) {
        throw new Error('No response from AI');
    }

    // Parse JSON from response
    try {
        // Clean the response - remove markdown code blocks if present
        // The model sometimes returns multiple ```json blocks with duplicated content,
        // so we extract just the first JSON code block's content
        let cleanedText = text.trim();
        const jsonBlockMatch = cleanedText.match(/```json\s*([\s\S]*?)```/);
        if (jsonBlockMatch) {
            cleanedText = jsonBlockMatch[1].trim();
        } else {
            // Fallback: strip leading/trailing fences
            if (cleanedText.startsWith('```json')) {
                cleanedText = cleanedText.slice(7);
            } else if (cleanedText.startsWith('```')) {
                cleanedText = cleanedText.slice(3);
            }
            if (cleanedText.endsWith('```')) {
                cleanedText = cleanedText.slice(0, -3);
            }
            cleanedText = cleanedText.trim();
        }

        // If the response was truncated, try to repair the JSON
        if (finishReason === 'MAX_TOKENS') {
            cleanedText = repairTruncatedJson(cleanedText);
        }

        let briefs = JSON.parse(cleanedText);

        // Post-process: merge orphaned carousel fragments back into their parent briefs.
        // The AI sometimes splits carousel data into a separate object (one with base fields
        // but no carouselCards, and another with only carousel story fields).
        briefs = mergeOrphanedCarouselFragments(briefs);

        // Add pillar info and brand colors to each brief
        return briefs.map(brief => {
            const enrichedBrief = {
                ...brief,
                pillarId: pillar.id,
                pillarName: pillar.name,
                pillarColor: pillar.color || '#6366F1',
                // Store brand color palette for display in brief cards
                brandColors: brandColors,
                // Store pillar content guidelines so they're available at image generation time
                pillarDescription: pillar.description || '',
                pillarGuidelines: {
                    toneOfVoice: pillar.contentGuidelines?.toneOfVoice || [],
                    keyMessages: pillar.contentGuidelines?.keyMessages || [],
                    exampleTopics: pillar.contentGuidelines?.exampleTopics || []
                }
            };

            // Match product to get productId and productPageUrl
            if (brief.product && products && products.length > 0) {
                const matchedProduct = findMatchingProduct(brief.product, products);
                if (matchedProduct) {
                    enrichedBrief.productId = matchedProduct.id;
                    enrichedBrief.productPageUrl = enrichedBrief.productPageUrl || matchedProduct.productUrl || '';
                }
            }

            return enrichedBrief;
        });
    } catch (parseError) {
        console.error('Failed to parse AI response:', parseError);
        console.error('Raw response:', text);
        throw new Error('Failed to parse AI response as JSON');
    }
}

/**
 * Generate event-themed briefs
 * @param {Object} brand - Brand data
 * @param {Array} events - Selected calendar events
 * @param {Array} pillars - Marketing pillars
 * @param {Object} config - Plan config including contentTypeDistribution
 */
async function generateEventBriefs(brand, events, pillars, config = {}) {
    if (!events || events.length === 0) return [];

    const eventBriefs = [];
    const activePillars = pillars.filter(p => !p.isArchived);

    // Get content type distribution from config
    const contentTypeDist = config.contentTypeDistribution || { image: 50, video: 30, carousel: 20 };

    // Determine allowed output types based on distribution
    // If a content type has 0%, don't include it as an option
    const allowedOutputTypes = [];
    if (contentTypeDist.image > 0) allowedOutputTypes.push('image');
    if (contentTypeDist.video > 0) allowedOutputTypes.push('video');
    if (contentTypeDist.carousel > 0) allowedOutputTypes.push('carousel');

    // Default to image-only if no types are allowed (shouldn't happen but safety check)
    const outputTypeOptions = allowedOutputTypes.length > 0 ? allowedOutputTypes : ['image'];
    const outputTypeString = outputTypeOptions.map(t => `"${t}"`).join(' | ');

    for (const event of events) {
        const briefCount = event.briefCount || 1;

        // Pick pillars for event briefs
        const pillar = activePillars[eventBriefs.length % activePillars.length] || activePillars[0];

        const prompt = `You are a senior creative strategist generating content briefs for an upcoming event/promotion.

## BRAND CONTEXT
**Brand:** ${brand?.name || 'Unknown Brand'}
**Industry:** ${brand?.industry || 'Not specified'}
**Target Audience:** ${brand?.targetAudience || 'Not specified'}
**Brand Voice:** ${brand?.brandVoice || 'Professional and engaging'}

## EVENT DETAILS
**Event:** ${event.name}
**Type:** ${event.type}
**Date:** ${event.dateString}
${event.description ? `**Description:** ${event.description}` : ''}

## MARKETING PILLAR TO USE
**Pillar:** ${pillar.name}
**Description:** ${pillar.description || 'Not specified'}

## CONTENT TYPE CONSTRAINTS
You may ONLY use these output types: ${outputTypeString}
${outputTypeOptions.length === 1 ? `Generate ALL briefs as "${outputTypeOptions[0]}" content.` : `Distribute briefs across the allowed types based on what works best for each piece.`}

## TASK
Generate ${briefCount} content brief(s) that tie into this event/promotion while aligning with the brand and pillar.

For each brief, provide:
{
  "funnelStage": "TOFU" | "MOFU" | "BOFU",
  "outputType": ${outputTypeString},
  "product": "specific product or offer to feature",
  "targetAudience": "specific audience segment",
  "objective": "what this creative should achieve",
  "marketingAngle": "event-themed angle",
  "desiredEmotion": "primary emotion to evoke",
  "selectedHeadline": "event-themed headline (under 10 words)",
  "cta": "clear call-to-action",
  "tone": "festive/appropriate tone for the event",
  "visualStyle": "description of event-themed visual approach",
  "sceneDescription": "detailed scene description incorporating event theme",
  "hookDescription": "the specific hook used in this creative",
  "caption": "engaging social media caption for the event (2-4 sentences)",
  "hashtags": ["relevant", "event-themed", "hashtags", "5-8 total"],
  "eventTie": "${event.name}"
}

## IMPORTANT
- Make the creative feel seasonal/relevant to the event
- Don't be too generic - tie specifically to the event
- Consider pre-event hype, during-event, and post-event content where relevant
- ONLY use outputType values from the allowed list: ${outputTypeOptions.join(', ')}

Return ONLY a valid JSON array. No markdown.`;

        try {
            // Add delay between event brief requests to avoid rate limiting
            if (eventBriefs.length > 0) {
                await delay(RATE_LIMIT_CONFIG.delayBetweenRequests);
            }

            const response = await fetchWithRetry(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    contents: [{ parts: [{ text: prompt }] }],
                    generationConfig: {
                        temperature: 0.8,
                        topK: 40,
                        topP: 0.95,
                        maxOutputTokens: 4096
                    }
                })
            });

            if (response.ok) {
                const data = await response.json();
                const text = data.candidates?.[0]?.content?.parts?.[0]?.text;

                if (text) {
                    let cleanedText = text.trim();
                    const evtJsonBlockMatch = cleanedText.match(/```json\s*([\s\S]*?)```/);
                    if (evtJsonBlockMatch) {
                        cleanedText = evtJsonBlockMatch[1].trim();
                    } else {
                        if (cleanedText.startsWith('```json')) {
                            cleanedText = cleanedText.slice(7);
                        } else if (cleanedText.startsWith('```')) {
                            cleanedText = cleanedText.slice(3);
                        }
                        if (cleanedText.endsWith('```')) {
                            cleanedText = cleanedText.slice(0, -3);
                        }
                        cleanedText = cleanedText.trim();
                    }

                    const briefs = JSON.parse(cleanedText);
                    const eventBrandColors = getBrandColorPalette(brand);
                    briefs.forEach(brief => {
                        eventBriefs.push({
                            ...brief,
                            pillarId: pillar.id,
                            pillarName: pillar.name,
                            pillarColor: pillar.color || '#6366F1',
                            brandColors: eventBrandColors,
                            isEventBrief: true,
                            eventName: event.name,
                            eventDate: event.dateString,
                            suggestedDate: event.dateString
                        });
                    });
                }
            }
        } catch (error) {
            console.error(`Failed to generate briefs for event ${event.name}:`, error);
        }
    }

    return eventBriefs;
}

/**
 * Generate all briefs for a content plan
 * @param {Object} plan - The content plan
 * @param {Array} pillars - Marketing pillars
 * @param {Object} brand - Brand data
 * @param {Function} onProgress - Progress callback
 * @param {Array} selectedEvents - Selected calendar events
 * @param {Array} selectedPromotions - Selected promotions
 * @param {Array} products - Available products
 * @param {Array} productAssignments - Product assignments per brief slot { id, pillarId, funnelStage, productId }
 */
export async function generateBulkBriefs(plan, pillars, brand, onProgress = null, selectedEvents = [], selectedPromotions = [], products = [], productAssignments = []) {
    const allBriefs = [];
    const { recommendations, config } = plan;
    const { byPillar, byFunnelStage } = recommendations;

    // If promotions are selected, add them to config for the prompt
    // We'll use the first promotion as the "active" one for now
    // (Future enhancement: could generate separate briefs per promotion)
    const activePromotion = selectedPromotions.length > 0 ? selectedPromotions[0] : null;
    const configWithPromotion = {
        ...config,
        activePromotion
    };

    // Calculate briefs needed per pillar
    const pillarBriefCounts = {};
    const activePillars = pillars.filter(p => !p.isArchived);

    activePillars.forEach(pillar => {
        const pillarRec = byPillar[pillar.id];
        if (pillarRec && pillarRec.recommended > 0) {
            // Distribute this pillar's count across funnel stages
            // Use Math.round and adjust to avoid over-allocation
            const total = pillarRec.recommended;

            // Calculate raw counts with Math.round
            let tofuCount = Math.round(total * (byFunnelStage.TOFU.percentage / 100));
            let mofuCount = Math.round(total * (byFunnelStage.MOFU.percentage / 100));
            let bofuCount = Math.round(total * (byFunnelStage.BOFU.percentage / 100));

            // Adjust to match total exactly
            const allocated = tofuCount + mofuCount + bofuCount;
            let diff = total - allocated;

            // Distribute any difference (prioritize BOFU, then MOFU, then TOFU)
            while (diff > 0) {
                if (bofuCount < total) { bofuCount++; diff--; }
                else if (mofuCount < total) { mofuCount++; diff--; }
                else if (tofuCount < total) { tofuCount++; diff--; }
                else break;
            }
            while (diff < 0) {
                if (tofuCount > 0) { tofuCount--; diff++; }
                else if (mofuCount > 0) { mofuCount--; diff++; }
                else if (bofuCount > 0) { bofuCount--; diff++; }
                else break;
            }

            // Calculate content type counts for this pillar
            const contentTypeDist = config.contentTypeDistribution || { image: 50, video: 30, carousel: 20 };
            const pillarTotal = tofuCount + mofuCount + bofuCount;
            let imageCount = Math.round(pillarTotal * (contentTypeDist.image / 100));
            let videoCount = Math.round(pillarTotal * (contentTypeDist.video / 100));
            let carouselCount = Math.round(pillarTotal * (contentTypeDist.carousel / 100));

            // Adjust rounding to match pillar total
            const typeTotal = imageCount + videoCount + carouselCount;
            let typeDiff = pillarTotal - typeTotal;
            while (typeDiff > 0) { imageCount++; typeDiff--; }
            while (typeDiff < 0) {
                if (imageCount > 0) { imageCount--; typeDiff++; }
                else if (videoCount > 0) { videoCount--; typeDiff++; }
                else { carouselCount--; typeDiff++; }
            }

            pillarBriefCounts[pillar.id] = {
                pillar,
                counts: {
                    TOFU: tofuCount,
                    MOFU: mofuCount,
                    BOFU: bofuCount
                },
                contentTypeCounts: {
                    image: imageCount,
                    video: videoCount,
                    carousel: carouselCount
                }
            };
        }
    });

    // Generate briefs for each pillar
    const pillarIds = Object.keys(pillarBriefCounts);
    let completedPillars = 0;
    const totalSteps = pillarIds.length + (selectedEvents.length > 0 ? 1 : 0);

    for (const pillarId of pillarIds) {
        const { pillar, counts, contentTypeCounts } = pillarBriefCounts[pillarId];

        try {
            // Add delay between pillar requests to avoid rate limiting
            if (completedPillars > 0) {
                if (onProgress) {
                    onProgress({
                        phase: 'waiting',
                        message: 'Pacing requests to avoid rate limits...',
                        current: completedPillars,
                        total: totalSteps
                    });
                }
                console.log(`Waiting ${RATE_LIMIT_CONFIG.delayBetweenRequests / 1000}s before next pillar...`);
                await delay(RATE_LIMIT_CONFIG.delayBetweenRequests);
            }

            if (onProgress) {
                onProgress({
                    phase: 'generating',
                    pillar: pillar.name,
                    current: completedPillars,
                    total: totalSteps
                });
            }

            // Filter product assignments for this pillar and prepare them for the prompt
            // This ensures the AI knows exactly which product to feature in each brief slot
            const pillarAssignments = productAssignments
                .filter(a => a.pillarId === pillarId)
                .map(a => {
                    // Extract slot index from the assignment ID (format: pillarId-stage-index)
                    const parts = a.id.split('-');
                    const slotIndex = parseInt(parts[parts.length - 1], 10);
                    return {
                        ...a,
                        slotIndex
                    };
                })
                .sort((a, b) => {
                    // Sort by funnel stage order, then by slot index
                    const stageOrder = { TOFU: 0, MOFU: 1, BOFU: 2 };
                    if (stageOrder[a.funnelStage] !== stageOrder[b.funnelStage]) {
                        return stageOrder[a.funnelStage] - stageOrder[b.funnelStage];
                    }
                    return a.slotIndex - b.slotIndex;
                });

            // Only skip generic product context if we have per-slot assignments
            // (the per-slot assignments will be passed separately with full product details)
            const hasPerSlotAssignments = pillarAssignments.length > 0;
            const briefs = await generatePillarBriefs(brand, pillar, counts, allBriefs, configWithPromotion, products, contentTypeCounts, hasPerSlotAssignments, pillarAssignments);
            allBriefs.push(...briefs);

            completedPillars++;
        } catch (error) {
            console.error(`Failed to generate briefs for pillar ${pillar.name}:`, error);
            // Continue with other pillars
        }
    }

    // Generate event-themed briefs
    if (selectedEvents && selectedEvents.length > 0) {
        // Add delay before event briefs to avoid rate limiting
        if (allBriefs.length > 0) {
            if (onProgress) {
                onProgress({
                    phase: 'waiting',
                    message: 'Preparing event briefs...',
                    current: completedPillars,
                    total: totalSteps
                });
            }
            console.log(`Waiting ${RATE_LIMIT_CONFIG.delayBetweenRequests / 1000}s before generating event briefs...`);
            await delay(RATE_LIMIT_CONFIG.delayBetweenRequests);
        }

        if (onProgress) {
            onProgress({
                phase: 'generating',
                pillar: 'Events & Promotions',
                current: completedPillars,
                total: totalSteps
            });
        }

        const eventBriefs = await generateEventBriefs(brand, selectedEvents, pillars, config);
        allBriefs.push(...eventBriefs);
    }

    // Track assignment usage per pillar+stage to match briefs to assignments
    const assignmentCounters = {};

    // Add sequence numbers, content type, promotion info, and assigned product
    allBriefs.forEach((brief, index) => {
        brief.sequence = index + 1;
        brief.contentType = config.includePaid && brief.funnelStage === 'BOFU' ? 'paid' : 'organic';
        brief.status = 'ai_generated';

        // Mark promotional briefs
        if (activePromotion) {
            brief.hasPromotion = true;
            brief.promotionId = activePromotion.id;
            brief.promotionName = activePromotion.name;
        }

        // Assign product from productAssignments
        if (productAssignments && productAssignments.length > 0 && brief.pillarId) {
            const key = `${brief.pillarId}-${brief.funnelStage}`;
            assignmentCounters[key] = (assignmentCounters[key] || 0);

            // Find matching assignment for this pillar+stage+index
            const assignmentId = `${brief.pillarId}-${brief.funnelStage}-${assignmentCounters[key]}`;
            const assignment = productAssignments.find(a => a.id === assignmentId);

            if (assignment?.productId) {
                const product = products.find(p => p.id === assignment.productId);
                if (product) {
                    brief.productId = product.id;
                    brief.product = product.name;
                    brief.productPageUrl = product.productUrl || '';
                    // Also set in nested brief if it exists
                    if (brief.brief) {
                        brief.brief.product = product.name;
                        brief.brief.productId = product.id;
                        brief.brief.productPageUrl = product.productUrl || '';
                    }
                }
            }

            assignmentCounters[key]++;
        }
    });

    if (onProgress) {
        onProgress({
            phase: 'complete',
            total: allBriefs.length
        });
    }

    return allBriefs;
}

// ============================================================================
// A/B TEST SUGGESTIONS
// ============================================================================

/**
 * Build prompt for A/B test suggestions
 */
function buildABTestSuggestionPrompt(briefs) {
    const briefSummaries = briefs.map((b, i) => ({
        index: i,
        funnelStage: b.funnelStage,
        pillar: b.pillarName,
        headline: b.brief?.selectedHeadline || b.selectedHeadline,
        angle: b.brief?.marketingAngle || b.marketingAngle,
        outputType: b.outputType
    }));

    return `You are an A/B testing expert analyzing content briefs to suggest high-impact tests.

## BRIEFS TO ANALYZE
${JSON.stringify(briefSummaries, null, 2)}

## A/B TEST TYPES
1. **same_visual_different_text**: Test headline, CTA, or copy while keeping visual constant
   - Low effort, quick insights
   - Best for: Testing message resonance

2. **completely_different**: Test entirely different creative approaches
   - Higher effort, bigger learnings
   - Best for: Testing creative direction

## SUGGESTION CRITERIA
Prioritize tests for:
- BOFU content (high conversion impact)
- Briefs with strong but unvalidated hooks
- Similar briefs that could compete against each other
- Novel angles worth validating

## OUTPUT FORMAT
Return a JSON array of test suggestions:
{
  "suggestions": [
    {
      "briefIndices": [0, 1],
      "testType": "same_visual_different_text" | "completely_different",
      "testElement": "headline" | "cta" | "visual" | "angle",
      "hypothesis": "What we're trying to learn",
      "expectedLearning": "Specific insight we'll gain",
      "potentialImpact": "high" | "medium" | "low",
      "effort": "low" | "medium" | "high",
      "recommendation": "Brief explanation of why this test matters"
    }
  ]
}

Suggest 3-5 high-impact tests. Return ONLY valid JSON.`;
}

/**
 * Suggest A/B tests based on generated briefs
 */
export async function suggestABTests(briefs) {
    if (!briefs || briefs.length < 2) {
        return { suggestions: [] };
    }

    const prompt = buildABTestSuggestionPrompt(briefs);

    try {
        const response = await fetchWithRetry(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                contents: [{ parts: [{ text: prompt }] }],
                generationConfig: {
                    temperature: 0.7,
                    topK: 40,
                    topP: 0.95,
                    maxOutputTokens: 4096
                }
            })
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        const text = data.candidates?.[0]?.content?.parts?.[0]?.text;

        if (!text) {
            throw new Error('No response from AI');
        }

        // Parse JSON with robust handling for AI-generated content
        let cleanedText = text.trim();
        const carouselJsonBlockMatch = cleanedText.match(/```json\s*([\s\S]*?)```/);
        if (carouselJsonBlockMatch) {
            cleanedText = carouselJsonBlockMatch[1].trim();
        } else {
            if (cleanedText.startsWith('```json')) {
                cleanedText = cleanedText.slice(7);
            } else if (cleanedText.startsWith('```')) {
                cleanedText = cleanedText.slice(3);
            }
            if (cleanedText.endsWith('```')) {
                cleanedText = cleanedText.slice(0, -3);
            }
            cleanedText = cleanedText.trim();
        }

        // Try to repair if truncated
        cleanedText = repairTruncatedJson(cleanedText);

        let result;
        try {
            result = JSON.parse(cleanedText);
        } catch (parseError) {
            // If parsing fails due to control characters in strings, try to sanitize
            // Replace newlines/tabs inside string values with spaces
            const sanitized = cleanedText.replace(
                /"(?:[^"\\]|\\.)*"/g,
                (match) => match.replace(/[\n\r\t]/g, ' ')
            );
            result = JSON.parse(sanitized);
        }

        // Enrich suggestions with brief data
        if (result.suggestions) {
            result.suggestions = result.suggestions.map(suggestion => ({
                ...suggestion,
                briefs: suggestion.briefIndices.map(i => briefs[i]).filter(Boolean),
                testTypeInfo: AB_TEST_TYPES[suggestion.testType]
            }));
        }

        return result;
    } catch (error) {
        console.error('Failed to suggest A/B tests:', error);
        return { suggestions: [], error: error.message };
    }
}

/**
 * Generate A/B variant for a brief
 */
export async function generateABVariant(brief, testElement) {
    const prompt = `You are creating an A/B test variant for a content brief.

## ORIGINAL BRIEF
${JSON.stringify(brief, null, 2)}

## TEST ELEMENT: ${testElement}

## TASK
Create a variant that tests the ${testElement}.
- Keep everything else the same
- Make the change meaningful enough to measure
- The variant should be equally strong, not obviously worse

Return a JSON object with:
{
  "variant": {
    // Full brief with the ${testElement} changed
  },
  "hypothesis": "What this variant tests",
  "expectedDifference": "How we expect results to differ"
}

Return ONLY valid JSON.

${COPY_STYLE_GUIDE}`;

    try {
        const response = await fetchWithRetry(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                contents: [{ parts: [{ text: prompt }] }],
                generationConfig: {
                    temperature: 0.7,
                    maxOutputTokens: 8192
                }
            })
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        const text = data.candidates?.[0]?.content?.parts?.[0]?.text;

        // Parse and return
        let cleanedText = text.trim();
        if (cleanedText.startsWith('```')) {
            cleanedText = cleanedText.replace(/^```json?\n?/, '').replace(/```$/, '');
        }
        cleanedText = cleanedText.trim();

        // Repair if truncated
        cleanedText = repairTruncatedJson(cleanedText);

        try {
            return JSON.parse(cleanedText);
        } catch (parseError) {
            // If parsing fails due to control characters in strings, try to sanitize
            const sanitized = cleanedText.replace(
                /"(?:[^"\\]|\\.)*"/g,
                (match) => match.replace(/[\n\r\t]/g, ' ')
            );
            return JSON.parse(sanitized);
        }
    } catch (error) {
        console.error('Failed to generate A/B variant:', error);
        throw error;
    }
}

// ============================================================================
// BRIEF REFINEMENT
// ============================================================================

/**
 * Refine a brief based on user instructions
 * Tracks refinement history to prevent repeated mistakes
 */
export async function refineBrief(brief, userInstructions) {
    // Get existing refinement history
    const refinementHistory = brief.refinementHistory || [];
    const isCarousel = (brief.brief || brief).outputType === 'carousel';

    // Build context from previous refinements
    const historyContext = refinementHistory.length > 0
        ? `
## PREVIOUS REFINEMENTS (DO NOT REPEAT THESE MISTAKES)
The user has already provided the following feedback. Make sure your output addresses ALL of these requirements and does NOT revert to previous versions:
${refinementHistory.map((h, i) => `${i + 1}. "${h.instruction}" - Applied on ${new Date(h.timestamp).toLocaleDateString()}`).join('\n')}

CRITICAL: The user is now providing additional feedback. You must KEEP all previous refinements AND apply the new instructions.`
        : '';

    const carouselContext = isCarousel ? `
## CAROUSEL-SPECIFIC REFINEMENT
This is a carousel brief. When refining:
- Preserve the storyArcType and storyNarrative unless the user explicitly asks to change them
- Keep the carouselCards array with all per-card sceneDescriptions
- Each card's sceneDescription must remain detailed enough for standalone image generation (50-100 words)
- Maintain visual cohesion across cards
- Preserve cardRole values unless the user asks to restructure the story
` : '';

    const prompt = `You are refining a content brief based on user feedback.

## ORIGINAL BRIEF
${JSON.stringify(brief.brief || brief, null, 2)}
${historyContext}
${carouselContext}

## NEW USER INSTRUCTIONS
${userInstructions}

## TASK
Modify the brief according to the user's instructions while:
1. KEEPING all previous refinements (if any) - do NOT revert changes
2. Applying the new instructions on top of existing changes
3. Maintaining the same funnel stage and pillar context
4. Ensuring professional quality
5. Preserving the original intent where not contradicted by user instructions

## IMPORTANT RULES
- If previous refinements exist, your output MUST reflect those changes PLUS the new instructions
- Never ignore or revert previous user feedback
- If new instructions conflict with previous ones, the NEW instructions take priority

Return the complete updated brief as JSON. Return ONLY valid JSON.`;

    try {
        const response = await fetchWithRetry(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                contents: [{ parts: [{ text: prompt }] }],
                generationConfig: {
                    temperature: 0.6,
                    maxOutputTokens: isCarousel ? 4096 : 2048
                }
            })
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        const text = data.candidates?.[0]?.content?.parts?.[0]?.text;

        // Parse and return
        let cleanedText = text.trim();
        if (cleanedText.startsWith('```')) {
            cleanedText = cleanedText.replace(/^```json?\n?/, '').replace(/```$/, '');
        }
        cleanedText = cleanedText.trim();

        let refinedBrief;
        try {
            refinedBrief = JSON.parse(cleanedText);
        } catch (parseError) {
            // If parsing fails due to control characters in strings, try to sanitize
            const sanitized = cleanedText.replace(
                /"(?:[^"\\]|\\.)*"/g,
                (match) => match.replace(/[\n\r\t]/g, ' ')
            );
            refinedBrief = JSON.parse(sanitized);
        }

        // Add current instruction to refinement history
        const updatedHistory = [
            ...refinementHistory,
            {
                instruction: userInstructions,
                timestamp: Date.now()
            }
        ];

        return {
            ...brief,
            brief: refinedBrief,
            refinementHistory: updatedHistory,
            isUserRefined: true,
            status: 'refined'
        };
    } catch (error) {
        console.error(`[${new Date().toISOString()}] Failed to refine brief:`, error);
        throw error;
    }
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
    FUNNEL_STAGE_CONTEXT,
    CONTENT_TYPE_GUIDANCE,
    AB_TEST_TYPES
};
